import { useRef, useState } from "react";
import { Input, InputRef } from "antd";
import { ColumnType } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";

export function useSearchTableNoFilter<T extends { key: React.Key }>(data: T[], tableRef: React.RefObject<any>) {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [matchIndexes, setMatchIndexes] = useState<number[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [enteredValue, setEnteredValue] = useState(""); // 🔑 lo que se ingresó con enter
  const searchInput = useRef<InputRef>(null);

  const scrollToMatch = (index: number) => {
    const realIndex = matchIndexes[index];
    if (realIndex === undefined) return;

    const rowKey = data[realIndex]?.key;

    if (tableRef.current?.scrollToIndex) {
      setTimeout(() => {
        tableRef.current.scrollToIndex(realIndex);
      }, 300);
    } else if (rowKey) {
      const rowElement = tableRef.current?.querySelector(`tr[data-row-key="${rowKey}"]`);
      rowElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const getColumnSearchTableNoFilter = (dataIndex: keyof T): ColumnType<T> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => {
      const value = (selectedKeys[0] as string) ?? "";

      return (
        <div style={{ padding: 4, width: 320 }}>
          <Input
            ref={searchInput}
            placeholder={`Buscar ${String(dataIndex)}`}
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              setSelectedKeys(newValue ? [newValue] : []);
            }}
            onPressEnter={() => {
              setSearchText(value.toLowerCase());
              setSearchedColumn(dataIndex as string);
              setEnteredValue(value);
              confirm({ closeDropdown: false });
              const matches = data
                .map((item, idx) => (String(item[dataIndex]).toLowerCase().includes(value.toLowerCase()) ? idx : -1))
                .filter((i) => i !== -1);

              setMatchIndexes(matches);
              setCurrentMatchIndex(0);
              if (matches.length > 0) scrollToMatch(0);
            }}
            style={{ marginBottom: 8, display: "block" }}
          />

          {/* Coincidencias solo si se hizo Enter y hay resultados */}
          {enteredValue && matchIndexes.length > 0 && (
            <div
              style={{
                maxHeight: 100,
                overflowY: "auto",
                borderTop: "1px solid #e8e8e8",
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              {matchIndexes.map((idx, i) => (
                <div key={idx} style={{ marginBottom: 4 }}>
                  <button
                    onClick={() => {
                      setCurrentMatchIndex(i);
                      scrollToMatch(i);
                    }}
                    style={{
                      fontSize: "10px",
                      display: "block",
                      background: "none",
                      border: "none",
                      // color: "#1890ff",
                      textAlign: "left",
                      cursor: "pointer",
                      padding: 0,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {String((data[idx] as any)[dataIndex])} - {String((data[idx] as any)["LAB"])}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
          fontSize: "16px",
          opacity: 1,
        }}
      />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    onFilter: () => true,
    render: (text: string, record) =>
      searchedColumn === dataIndex && searchText ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--primary)",
            padding: 0,
            color: "#fff",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() || ""}
        />
      ) : (
        text
      ),
  });

  return {
    getColumnSearchTableNoFilter,
    searchText,
    searchedColumn,
    currentMatchIndex,
  };
}
